import { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { getWorkflowData, getCurrentStatus, selectVersion } from "store/editor";
import { isUserAdmin, isUserApplicant, getUserInfo } from "utils/userHelpers";

const STORAGE_KEY_PREFIX = "editorCoverage";
const PERSIST_INTERVAL_MS = 5000;
const MERGE_GAP_TOLERANCE = 1.5; // seconds — bridges gaps from timeupdate interval
const STALE_ENTRY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Builds the localStorage key for a given workflow + user + stage.
 * Each workflow stage gets independent tracking.
 */
const getStorageKey = (workflowId, userId, workflowStatus) =>
  `${STORAGE_KEY_PREFIX}_${workflowId}_${userId}_${workflowStatus}`;

/**
 * Merges a new [start, end] segment into a sorted, non-overlapping segment list.
 * Optimized: extends the last segment in-place for continuous playback (common case).
 * Falls back to full merge for seeks/out-of-order.
 */
const mergeSegment = (segments, start, end) => {
  // Fast path: extend last segment (covers ~95% of cases during continuous playback)
  if (segments.length > 0) {
    const last = segments[segments.length - 1];
    if (start >= last[0] && start <= last[1] + MERGE_GAP_TOLERANCE) {
      last[1] = Math.max(last[1], end);
      return segments;
    }
    // Append if clearly after the last segment (sequential playback after a seek)
    if (start > last[1] + MERGE_GAP_TOLERANCE) {
      segments.push([start, end]);
      return segments;
    }
  }

  // Slow path: full sort + merge (rare — only on backward seeks)
  const newSegments = [...segments, [start, end]];
  newSegments.sort((a, b) => a[0] - b[0]);

  const merged = [newSegments[0]];
  for (let i = 1; i < newSegments.length; i++) {
    const last = merged[merged.length - 1];
    if (newSegments[i][0] <= last[1] + MERGE_GAP_TOLERANCE) {
      last[1] = Math.max(last[1], newSegments[i][1]);
    } else {
      merged.push(newSegments[i]);
    }
  }

  return merged;
};

/**
 * Calculate total seconds covered by segments.
 */
export const getTotalPlayed = (segments) =>
  segments.reduce((sum, [start, end]) => sum + (end - start), 0);

/**
 * Load coverage data from localStorage with shape validation.
 */
const loadCoverage = (workflowId, userId, workflowStatus) => {
  try {
    const key = getStorageKey(workflowId, userId, workflowStatus);
    const raw = localStorage.getItem(key);
    if (raw) {
      const data = JSON.parse(raw);
      // Validate expected shape
      return {
        ...data,
        playedSegments: Array.isArray(data.playedSegments) ? data.playedSegments : [],
        viewedSnippetIds: Array.isArray(data.viewedSnippetIds) ? data.viewedSnippetIds : [],
        totalSnippetCount: typeof data.totalSnippetCount === "number" ? data.totalSnippetCount : 0,
        audioDuration: typeof data.audioDuration === "number" ? data.audioDuration : 0,
        sessionCount: typeof data.sessionCount === "number" ? data.sessionCount : 0,
      };
    }
  } catch (e) {
    // corrupted data, start fresh
  }
  return null;
};

/**
 * Save coverage data to localStorage.
 */
const saveCoverage = (workflowId, userId, workflowStatus, data) => {
  try {
    const key = getStorageKey(workflowId, userId, workflowStatus);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    // storage full or unavailable
  }
};

/**
 * Module-level live cache of coverage data.
 * The hook writes here on every update so getCoverageData always returns
 * the latest in-memory state, not the potentially stale localStorage snapshot.
 */
const liveCoverageCache = new Map();

const getLiveCacheKey = (workflowId, workflowStatus) =>
  `${workflowId}_${workflowStatus}`;

/**
 * Remove coverage data from localStorage (called after submission).
 */
export const removeCoverage = (workflowId, userId, workflowStatus) => {
  try {
    const key = getStorageKey(workflowId, userId, workflowStatus);
    localStorage.removeItem(key);
  } catch (e) {
    // noop
  }
  liveCoverageCache.delete(getLiveCacheKey(workflowId, workflowStatus));
};

/**
 * Get current coverage data for a workflow + user + stage (used at submission time).
 * Reads from the in-memory live cache first, falls back to localStorage.
 */
export const getCoverageData = (workflowId, workflowStatus) => {
  const userInfo = getUserInfo();
  const userId = userInfo?.user?.user_id;
  if (!workflowId || !userId || !workflowStatus) return null;

  // Prefer live in-memory data (always up-to-date)
  const cacheKey = getLiveCacheKey(workflowId, workflowStatus);
  const live = liveCoverageCache.get(cacheKey);
  if (live) return live;

  return loadCoverage(workflowId, userId, workflowStatus);
};

/**
 * Prune stale coverage entries older than 30 days from localStorage.
 * Runs once per hook mount to prevent unbounded growth.
 */
const pruneOldCoverage = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          const lastSession = new Date(data.lastSessionAt).getTime();
          if (Date.now() - lastSession > STALE_ENTRY_MAX_AGE_MS) {
            keysToRemove.push(key);
          }
        } catch (e) {
          keysToRemove.push(key); // corrupted entry, remove
        }
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    // noop
  }
};

/**
 * Hook that tracks audio playback coverage and transcript scroll coverage
 * for non-admin editors. Persists to localStorage to survive tab close/reopen.
 * Tracks independently per workflow stage (First_Review, QA_Review, etc.).
 *
 * @param {React.RefObject} audioPlayerRef - ref to the audio player component
 */
export default function useEditorCoverageTracker(audioPlayerRef) {
  const workflowData = useSelector(getWorkflowData);
  const workflowId = workflowData?.id;
  const workflowStatus = useSelector(getCurrentStatus);
  const version = useSelector(selectVersion);

  // Don't track the Not_Started stage (initial upload, no editor review needed)
  const shouldTrack = workflowStatus && workflowStatus !== "Not_Started";

  const coverageRef = useRef(null);
  const lastTimeRef = useRef(null);
  const persistTimerRef = useRef(null);
  const observerRef = useRef(null);
  const dirtyRef = useRef(false);
  const viewedSetRef = useRef(new Set());
  const observedElementsRef = useRef(new WeakSet());
  const audioElRef = useRef(null);
  const shouldSkip = useRef(isUserAdmin() || isUserApplicant());

  // Read user info lazily — localStorage may not be populated on first render
  const getUserData = useCallback(() => {
    const info = getUserInfo();
    return {
      userId: info?.user?.user_id,
      userName: `${info?.user?.first_name || ""} ${info?.user?.last_name || ""}`.trim(),
      userEmail: info?.user?.email,
      orgRole: info?.org_role,
    };
  }, []);

  // Prune stale entries once on mount
  useEffect(() => {
    pruneOldCoverage();
  }, []);

  // Initialize or restore coverage data (per workflow + stage)
  useEffect(() => {
    const { userId, userName, userEmail, orgRole } = getUserData();
    if (shouldSkip.current || !workflowId || !userId || !shouldTrack) return;

    const existing = loadCoverage(workflowId, userId, workflowStatus);
    coverageRef.current = existing || {
      workflowId,
      workflowStatus,
      userId,
      userName,
      userEmail,
      orgRole,
      playedSegments: [],
      viewedSnippetIds: [],
      totalSnippetCount: 0,
      audioDuration: 0,
      editCount: 0,
      editorOpenedAt: new Date().toISOString(),
      sessionCount: 0,
    };

    // Rebuild Set from loaded data for O(1) lookups
    viewedSetRef.current = new Set(coverageRef.current.viewedSnippetIds);

    // Increment session count (tracks window close/reopen)
    coverageRef.current.sessionCount = (coverageRef.current.sessionCount || 0) + 1;
    coverageRef.current.lastSessionAt = new Date().toISOString();

    saveCoverage(workflowId, userId, workflowStatus, coverageRef.current);
    liveCoverageCache.set(getLiveCacheKey(workflowId, workflowStatus), coverageRef.current);
  }, [workflowId, workflowStatus, shouldTrack, getUserData]);

  // Track edit count as delta: edits in THIS stage = version_now - version_at_stage_start
  // stage_start_version comes from the backend (set when workflow transitions to this status)
  useEffect(() => {
    if (coverageRef.current && version != null && version > 0) {
      const stageStartVersion = workflowData?.stage_start_version;
      const baseline = stageStartVersion != null ? stageStartVersion : (coverageRef.current.initialVersion || version);
      // Persist the baseline once so it survives page reloads
      if (!coverageRef.current.initialVersion) {
        coverageRef.current.initialVersion = baseline;
      }
      coverageRef.current.editCount = Math.max(0, version - coverageRef.current.initialVersion);
      dirtyRef.current = true;
      liveCoverageCache.set(getLiveCacheKey(workflowId, workflowStatus), coverageRef.current);
    }
  }, [version, workflowId, workflowStatus, workflowData?.stage_start_version]);

  // Persist to localStorage only when dirty
  const persistToStorage = useCallback(() => {
    const { userId } = getUserData();
    if (dirtyRef.current && coverageRef.current && workflowId && userId && shouldTrack) {
      saveCoverage(workflowId, userId, workflowStatus, coverageRef.current);
      dirtyRef.current = false;
    }
  }, [workflowId, workflowStatus, shouldTrack, getUserData]);

  // Audio playback tracking via timeupdate
  useEffect(() => {
    const { userId } = getUserData();
    if (shouldSkip.current || !workflowId || !userId || !shouldTrack) return;

    const getAudio = () => audioPlayerRef?.current?.audio?.current;

    const onTimeUpdate = () => {
      const audio = getAudio();
      if (!audio || !coverageRef.current) return;

      const currentTime = audio.currentTime;
      const duration = audio.duration;

      if (duration && !isNaN(duration)) {
        coverageRef.current.audioDuration = duration;
      }

      if (lastTimeRef.current !== null) {
        const diff = currentTime - lastTimeRef.current;
        // Only track continuous playback (not seeks)
        if (diff > 0 && diff < 3) {
          coverageRef.current.playedSegments = mergeSegment(
            coverageRef.current.playedSegments,
            lastTimeRef.current,
            currentTime
          );
          dirtyRef.current = true;
        }
      }

      lastTimeRef.current = currentTime;
    };

    const onSeeked = () => {
      const audio = getAudio();
      if (audio) {
        lastTimeRef.current = audio.currentTime;
      }
    };

    const onPause = () => {
      lastTimeRef.current = null;
      persistToStorage();
    };

    const onPlay = () => {
      const audio = getAudio();
      if (audio) {
        lastTimeRef.current = audio.currentTime;
      }
    };

    // Attach listeners with delay to ensure audio element is mounted
    const attachTimeout = setTimeout(() => {
      const audio = getAudio();
      if (!audio) return;

      audioElRef.current = audio;
      audio.addEventListener("timeupdate", onTimeUpdate);
      audio.addEventListener("seeked", onSeeked);
      audio.addEventListener("pause", onPause);
      audio.addEventListener("play", onPlay);
    }, 1000);

    persistTimerRef.current = setInterval(persistToStorage, PERSIST_INTERVAL_MS);

    return () => {
      clearTimeout(attachTimeout);
      clearInterval(persistTimerRef.current);
      // Always clean up from stored audio ref (handles timeout race)
      if (audioElRef.current) {
        audioElRef.current.removeEventListener("timeupdate", onTimeUpdate);
        audioElRef.current.removeEventListener("seeked", onSeeked);
        audioElRef.current.removeEventListener("pause", onPause);
        audioElRef.current.removeEventListener("play", onPlay);
        audioElRef.current = null;
      }
      persistToStorage();
    };
  }, [workflowId, workflowStatus, shouldTrack, getUserData, audioPlayerRef, persistToStorage]);

  // Scroll/visibility tracking via IntersectionObserver
  useEffect(() => {
    const { userId } = getUserData();
    if (shouldSkip.current || !workflowId || !userId || !shouldTrack) return;

    // Reset observed elements tracking for this stage
    observedElementsRef.current = new WeakSet();

    const observeNewSnippets = () => {
      // Use .transcript-data to select only snippet containers, not individual word spans
      const snippets = document.querySelectorAll(".transcript-data[data-snippet-id]");
      if (coverageRef.current) {
        coverageRef.current.totalSnippetCount = snippets.length;
      }
      snippets.forEach((el) => {
        if (!observedElementsRef.current.has(el)) {
          observedElementsRef.current.add(el);
          observerRef.current?.observe(el);
        }
      });
    };

    const setupObserver = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (!coverageRef.current) return;

          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              const snippetId = entry.target.dataset.snippetId;
              if (snippetId && !viewedSetRef.current.has(snippetId)) {
                viewedSetRef.current.add(snippetId);
                coverageRef.current.viewedSnippetIds.push(snippetId);
                dirtyRef.current = true;
              }
            }
          });
        },
        { threshold: 0.5 }
      );

      observeNewSnippets();
    };

    // Debounced MutationObserver callback — only observe genuinely new elements
    let mutationDebounceTimer = null;
    const mutationObserver = new MutationObserver(() => {
      clearTimeout(mutationDebounceTimer);
      mutationDebounceTimer = setTimeout(observeNewSnippets, 500);
    });

    // Wait for transcript to be rendered
    const initTimeout = setTimeout(() => {
      setupObserver();

      const editorContainer = document.querySelector(".slate-editor") ||
        document.querySelector("[data-slate-editor]");
      if (editorContainer) {
        mutationObserver.observe(editorContainer, {
          childList: true,
          subtree: true,
        });
      }
    }, 2000);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(mutationDebounceTimer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      mutationObserver.disconnect();
      persistToStorage();
    };
  }, [workflowId, workflowStatus, shouldTrack, getUserData, persistToStorage]);
}
